import { useCallback, useEffect, useMemo, useState } from "react"
import { useAsyncSelector } from "src/hooks/useAsyncSelector"
import { useRefresher } from "src/hooks/useRefresher"
import { inboxContactsRequestIdState } from "src/recoil/atoms/contact/inboxContactsRequestIdState"
import { selectedMyOrganizationSelector } from "src/recoil/selectors/organization/selectedMyOrganizationSelector"
import { useContactDetailDataRefresher } from "src/recoil/hooks/refresher/useContactDetailDataRefresher"
import { useFetcher } from "src/hooks/useFetcher"
import { CustomAlert } from "src/utils/CustomAlert"
import { useRequestData } from "src/recoil/hooks/useRequestData"
import { updateFavoriteContactInbox } from "src/apis/contact/updateFavoriteContactInbox"
import { useResetter } from "./contact/create/useResetter"
import { ContactFilterValue } from "src/types/contact/ContactFilter"
import { myProfileSelectorFamily } from "src/recoil/selectors/organization/myProfile/myProfileSelectorFamily"
import { trashContact } from "src/apis/contact/trashContact"
import { useBottomTabBadgeData } from "src/recoil/hooks/bottomTab/useBottomTabBadgeData"
import { unreadThreadCountRequestIdState } from "src/recoil/atoms/contact/unreadThreadCountRequestIdState"
import { inboxContactsConcatSelectorFamily } from "src/recoil/selectors/contact/inboxContactsConcatSelectorFamily"
import { inboxContactsConcatRequestIdState } from "src/recoil/atoms/contact/inboxContactsConcatRequestIdState"
import { contactReplyThreadsRequestIdState } from "src/recoil/atoms/contact/contactReplyThreadsRequestIdState"
import { hasUnreadThreadMessageRequestIdState } from "src/recoil/atoms/contact/hasUnreadThreadMessageRequestIdState"
import { useRecoilState, useRecoilValue, useResetRecoilState } from "recoil"
import { inboxContactFavoriteFlgsCacheState } from "src/recoil/atoms/contact/contactFavoriteFlgsCacheState"
import { wait } from "src/utils/wait"
import { defaultContactFilterState } from "src/recoil/atoms/contact/defaultContactFilterState"
import { useFocusEffect } from "@react-navigation/native"
import { GetInboxContactsSuccess } from "src/apis/contact/getInboxContacts"
import { requestDataSelector } from "src/recoil/selectors/auth/requestDataSelector"
import { organizationDetailSelectorFamily } from "src/recoil/selectors/organization/organizationDetailSelectorFamily"
import { organizationRefreshState } from "src/recoil/atoms/organization/organizationRefreshPermissionState"
import { organizationDetailRequestIdState } from "src/recoil/atoms/organization/organizationDetailRequestIdState"
import { teamPermissionRequestIdState } from "src/recoil/atoms/organization/teamPermissionRequestIdState"
import { useIncrementalUnreadCount } from "src/hooks/useIncrementalUnreadCount"

export const useInboxData = () => {
  const [searchText, setSearchText] = useState("")
  const [confirmedSearchText, setConfirmedSearchText] = useState("")
  const [filters, setFilters] = useState<ContactFilterValue[]>([])
  const [defaultFilters, setDefaultFilters] = useRecoilState(defaultContactFilterState)
  useEffect(() => {
    if (defaultFilters != null) {
      setFilters(defaultFilters)
      setDefaultFilters(undefined)
    }
  }, [defaultFilters, setDefaultFilters, setFilters])
  const { value: selectedOrganization } = useAsyncSelector(selectedMyOrganizationSelector)
  const organizationId = useMemo(() => selectedOrganization?.id, [selectedOrganization])
  const { value: organization } = useAsyncSelector(organizationDetailSelectorFamily(organizationId))
  const { value: myProfile } = useAsyncSelector(myProfileSelectorFamily(organizationId))
  const myMemberId = useMemo(() => myProfile?.memberId, [myProfile])
  const [nextTokens, setNextTokens] = useState<string[]>([])
  useEffect(() => {
    setNextTokens([])
  }, [organizationId, setNextTokens])
  const setConfirmedSearchTextHelper = useCallback((text: string) => {
    setNextTokens([])
    setConfirmedSearchText(text)
  }, [])
  const params = useMemo(
    () => ({
      searchText: confirmedSearchText !== "" ? confirmedSearchText : undefined,
      filters: filters.length !== 0 ? filters : undefined,
    }),
    [confirmedSearchText, filters]
  )
  const { value: getContacts, isLoading: isLoadingContacts } = useAsyncSelector(
    inboxContactsConcatSelectorFamily({ ...params, nextTokens })
  )
  const [contacts, setContacts] = useState<GetInboxContactsSuccess>()
  const [favoriteFlgsCache, setFavoriteFlgsCache] = useRecoilState(inboxContactFavoriteFlgsCacheState)

  // totalをもとに次のページが存在するかどうかを判定
  // NextTokenは最後のページのレスポンスにも含まれてしまう
  // そのため、totalとcontactsの数を比較して次のページが存在するかどうかを判定する
  const hasNextPage = useMemo(() => {
    if (!getContacts || !getContacts.total) return false
    const currentContactsCount = getContacts?.contacts?.length || 0
    return currentContactsCount < getContacts.total
  }, [getContacts])

  const canLoadMore = useMemo(() => {
    return hasNextPage && !!getContacts?.nextToken
  }, [hasNextPage, getContacts?.nextToken])

  useEffect(() => {
    if (getContacts) {
      if (filters.includes("favorite") && getContacts.contacts) {
        const newContacts = getContacts.contacts.filter((e) => e.id && favoriteFlgsCache?.[e.id] !== false)
        setContacts({
          contacts: newContacts,
          nextToken: getContacts.nextToken,
          total: getContacts.total,
        })
      } else {
        setContacts(getContacts)
      }
    }
  }, [getContacts, filters, favoriteFlgsCache])

  const _refreshContacts = useRefresher(inboxContactsRequestIdState)
  const _refreshContactsConcat = useResetRecoilState(inboxContactsConcatRequestIdState)
  const _resetFavoriteFlgsCache = useResetRecoilState(inboxContactFavoriteFlgsCacheState)
  const { refreshUnreadContactCount } = useBottomTabBadgeData()
  const refreshContactReplyThreads = useRefresher(contactReplyThreadsRequestIdState)
  const refreshUnreadThreadCount = useRefresher(unreadThreadCountRequestIdState)
  const refreshHasUnreadThreadMessage = useRefresher(hasUnreadThreadMessageRequestIdState)
  const refreshOrganizationDetail = useRefresher(organizationDetailRequestIdState)
  const needRefreshOrganization = useRecoilValue(organizationRefreshState)
  useEffect(() => {
    if (needRefreshOrganization) {
      refreshOrganizationDetail()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needRefreshOrganization])

  const refreshPermission = useRefresher(teamPermissionRequestIdState)
  const refreshContacts = useCallback(() => {
    setNextTokens([])
    _refreshContacts()
    _refreshContactsConcat()
    _resetFavoriteFlgsCache()
    refreshUnreadContactCount()
    refreshContactReplyThreads()
    refreshUnreadThreadCount()
    refreshHasUnreadThreadMessage()
    refreshOrganizationDetail()
    refreshPermission()
  }, [
    _refreshContacts,
    _refreshContactsConcat,
    _resetFavoriteFlgsCache,
    refreshUnreadContactCount,
    refreshContactReplyThreads,
    refreshUnreadThreadCount,
    refreshHasUnreadThreadMessage,
    refreshOrganizationDetail,
    refreshPermission,
  ])

  const refreshFiltersContacts = useCallback(() => {
    setNextTokens([])
    _refreshContactsConcat()
  }, [_refreshContactsConcat])

  const setFiltersHelper = useCallback(
    (filters: ContactFilterValue[]) => {
      setFilters(filters)
      if (!filters.includes("favorite")) {
        refreshFiltersContacts()
      }
    },
    [refreshFiltersContacts]
  )

  useFocusEffect(
    useCallback(() => {
      if (filters.includes("favorite")) {
        refreshContacts()
      }
    }, [filters, refreshContacts])
  )

  const refreshContactDetailData = useContactDetailDataRefresher()

  const requestDataResult = useRequestData()

  const { fetch: execTrashContact } = useFetcher(
    useCallback(
      async (contactId: string) => {
        if (!requestDataResult.isOk) {
          return
        }
        const { accountId, accessToken } = requestDataResult.content
        if (accountId == null || accessToken == null || myMemberId == null) {
          return
        }
        const isConfirmed = await CustomAlert.confirm(
          "確認",
          "この連絡をゴミ箱へ移動しますか？\nこの連絡への返信も確認できなくなります。\nゴミ箱内の連絡は30日後に完全に削除されます。"
        )
        if (!isConfirmed) {
          return
        }
        const result = await trashContact({ accessToken, myMemberId, contactId })
        if (result.isOk) {
          await CustomAlert.alert("完了", "ゴミ箱に移動しました。")
          await wait(2000)
          refreshContacts()
        } else {
          await CustomAlert.alert("エラー", result.error.message)
        }
      },
      [requestDataResult, refreshContacts, myMemberId]
    )
  )

  const { fetch: execUpdateFavoriteContact } = useFetcher(
    useCallback(
      async ({ contactId, isFavorite }: { contactId: string; isFavorite: boolean }) => {
        if (!requestDataResult.isOk) {
          return
        }
        const { accountId, accessToken } = requestDataResult.content
        if (accountId == null || accessToken == null || myMemberId == null) {
          return
        }
        const result = await updateFavoriteContactInbox({ accessToken, myMemberId, contactId, isFavorite })
        if (!result.isOk) {
          CustomAlert.alert("エラー", result.error.message)
          return
        }
        setFavoriteFlgsCache((prev) => ({ ...prev, [contactId]: isFavorite }))
        if (!isFavorite && result.isOk && filters.includes("favorite") && contacts && contacts.contacts) {
          const newContacts = contacts.contacts.filter((e: { id: string }) => e.id && e.id !== contactId)
          setContacts({
            contacts: newContacts,
            nextToken: contacts.nextToken,
          })
        }
      },
      [requestDataResult, myMemberId, setFavoriteFlgsCache, filters, contacts]
    )
  )

  const resetCreateNewContactScreenData = useResetter()
  const request = useRecoilValue(requestDataSelector)

  // 増分的な未読数取得を使用
  const { unreadPerContact, resetUnreadCounts } = useIncrementalUnreadCount({
    contacts,
    request: request || null
  })

  // フィルタやリフレッシュ時に未読カウントをリセット
  useEffect(() => {
    resetUnreadCounts()
  }, [confirmedSearchText, filters, resetUnreadCounts])
  return {
    searchText,
    setSearchText: setSearchText,
    confirmedSearchText,
    setConfirmedSearchText: setConfirmedSearchTextHelper,
    filters,
    setFilters: setFiltersHelper,
    contacts,
    isLoadingContacts,
    unreadPerContact,
    refreshContacts,
    organizationId,
    myMemberId,
    refreshContactDetailData,
    execTrashContact,
    execUpdateFavoriteContact,
    resetCreateNewContactScreenData,
    setNextTokens,
    favoriteFlgsCache,
    organization,
    canLoadMore,
  }
}
